'use strict';

exports.__esModule = true;
exports._cryptOrUndefined = exports._getDelegationClaims = exports._getInfoTokenClaim = exports._getInfoTokenClaims = exports.getUserId = exports.updateAuthTokens = exports.isEmployee = exports.isDelegated = exports.isAuthenticated = exports.getShopperId = exports.getUserRealm = exports.getCustomerId = exports.getFederationPartnerId = exports.getEmployeeId = exports.getDelegateEmployeeId = exports.getDelegateCustomerId = exports.getClaimFromInfoToken = undefined;

var _cookie = require('./cookie');

var _crypto = require('./crypto');

var _authInfoToken = {};

var _authRealms = ['idp', 'jomax'];

var _authRealmIdMap = {
  idp: 'info_shopperId',
  jomax: 'info_accountName'
};

var _isTokenEmpty = function _isTokenEmpty(token) {
  return Object.keys(token).length < 1;
};

// Fetch the info_token for a particular realm from the browser
var _getInfoTokenClaims = function _getInfoTokenClaims(realm) {
  var claims = {};
  var cookieName = 'info_' + realm;
  var cookie = (0, _cookie.findCookie)(cookieName);
  if (cookie) {
    try {
      claims = JSON.parse(cookie);
    } catch (err) {} // eslint-disable-line no-empty
  }
  return claims;
};

/**
 *
 * @description - Finds/updates _authInfoToken with the newest auth token.
 *               If none are found, the _authInfoToken variable is cleared.
 *
 */
var updateAuthTokens = function updateAuthTokens() {
  var token = {};
  for (var i = 0; i < _authRealms.length; i++) {
    var realm = _authRealms[i];
    var infoToken = _getInfoTokenClaims(realm);

    // Update the info token if its newer or not currently defined
    if (!_isTokenEmpty(infoToken) && (_isTokenEmpty(token) || token.iat < infoToken.iat)) {
      token = infoToken;
    }
  }

  _authInfoToken = token;
};

var isAuthenticated = function isAuthenticated() {
  return !_isTokenEmpty(_authInfoToken);
};

// boolean value of whether the authenticated
// user is in a delegation scenario
var isDelegated = function isDelegated() {
  if (!isAuthenticated()) {
    return false;
  }

  var authType = _authInfoToken.auth;
  return authType && authType !== 'basic';
};

// Boolean value of whether an employee token is found
// on the browser or the newest token is an employee
// delegation scenario
var isEmployee = function isEmployee() {
  if (!isAuthenticated()) {
    return false;
  }

  var tokenType = _authInfoToken.typ;
  var authType = _authInfoToken.auth;
  // Check if the latest token is for jomax
  if (tokenType && tokenType === 'jomax') {
    return true;
  }

  // Check if the latest token is an employee impersonation
  if (authType && authType !== 'basic' && authType.charAt(0) === 'e') {
    return true;
  }

  // Check if there is a jomax token on the browser
  var jomaxToken = _getInfoTokenClaims('jomax');
  return !_isTokenEmpty(jomaxToken);
};

/**
 * @param { object } currentClaims - The current claims we are parsing for the delegate. E.g. {
 *  typ: 'idp',
 *  auth: 'e2s',
 *  del: {
 *    typ: 'jomax',
 *    info_accountName: 'FakeEmployeeId'
 *  },
 *  e2s: {
 *    type: 'idp',
 *    info_cid: 'FakeParentCustomerId'
 *  }
 * }
 * @param { string } delType - The type of delegation token to find. E.g. jomax or idp
 * @param { boolean } isRoot - It checks if we have traced inside del object atleast once.
 *                                     Defaults to True as we enter from root.
 * @description Given an authentication claim object and a delegation type,
 *              this function will recursively find the associated claims for
 *              that specific delegation type. This logic will only work for tokens
 *              with a max depth of 2 (i.e. e2s2s) given that we're looking ahead
 *              via claims.del.del to see if the 2nd parent matches the token type.
 * @returns { object } The matching delegate's claims. If a matching delegate of the
 *                     specified type is not found, then undefined is returned.
 * @example output {
 *    typ: 'jomax',
 *    info_accountName: 'FakeEmployeeId'
 *   }
 * @example 2
 * // for currentClaims :{
  typ: 'idp',
  auth: 'e2s2s',
  del: {
    typ: 'idp',
    auth: 'e2s',
    del: {
      typ: 'jomax',
      info_accountName: 'FakeEmployeeId',
      auth: 'basic'
    },
    e2s: {
      info_shopperId: '141234119',
      info_cid: 'FakeParentCustomerId'
    }
  },
  e2s2s: {
    info_shopperId: '225376367',
    info_cid: 'FakeCustomerId'
  }
};
* it looping inside the first block and to reach the furthest del block, then it gets control to second if,
* which sends the block of e2s currentclaims.
* returns {
      info_shopperId: '141234119',
      info_cid: 'FakeParentCustomerId'
    }
 */
var _getDelegationClaims = function _getDelegationClaims(currentClaims, delType) {
  var isRoot = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

  if (currentClaims.del) {
    if (currentClaims.del.typ === delType || currentClaims.del.del) {
      isRoot = false;
      return _getDelegationClaims(currentClaims.del, delType, isRoot);
    }
    if (delType === currentClaims.typ && !isRoot) return currentClaims[currentClaims.auth];
  }
  if (delType === currentClaims.typ && !isRoot) // this works for s2s
    return currentClaims;
};

var _cryptOrUndefined = function _cryptOrUndefined(encrypt, value) {
  if (!encrypt) {
    // If we're not encrypting... just return the value
    return value;
  }

  if (value) {
    // Return encrypted value
    return (0, _crypto.sha256)(value);
  }
};

// Fetch a claim from a delegation token
// (This will parse the token recursively)
var getDelegateClaim = function getDelegateClaim(claim, encrypt, delType) {
  if (isAuthenticated() && _authInfoToken.auth !== 'basic') {
    var delegationClaims = _getDelegationClaims(_authInfoToken, delType);
    if (delegationClaims) {
      return _cryptOrUndefined(encrypt, delegationClaims[claim]);
    }
  }
};

// Fetch the jomax (employee) username from the current token
var getDelegateEmployeeId = function getDelegateEmployeeId(encrypt) {
  return getDelegateClaim(_authRealmIdMap.jomax, encrypt, 'jomax');
};

// Returns the employee id if a jomax token is found
// If the newest token is an employee delegation token,
// the employee id will be returned.
var getEmployeeId = function getEmployeeId(encrypt) {
  if (isAuthenticated()) {
    var tokenType = _authInfoToken.typ;
    var authType = _authInfoToken.auth;
    var idClaimName = _authRealmIdMap.jomax;

    // Check if the latest token is an employee impersonation
    if (authType && authType !== 'basic' && authType.charAt(0) === 'e') {
      return getDelegateEmployeeId(encrypt);
    }

    // Check if the latest token is for jomax
    if (tokenType && tokenType === 'jomax') {
      return _cryptOrUndefined(encrypt, _authInfoToken[idClaimName]);
    }

    // Check if there is a jomax token on the browser
    var jomaxToken = _getInfoTokenClaims('jomax');
    if (!_isTokenEmpty(jomaxToken)) {
      return _cryptOrUndefined(encrypt, jomaxToken[idClaimName]);
    }
  }
};

// Search an auth token for a particular claim
var _getInfoTokenClaim = function _getInfoTokenClaim(tokenClaims, claim, type) {
  var tokenType = tokenClaims.typ;
  if (typeof type !== 'undefined' && tokenType && tokenType !== type) {
    return;
  }

  var rVal = void 0;
  var authType = tokenClaims.auth;
  // If this is a delegation token, search
  // sub claims recursively
  if (authType && authType !== 'basic') {
    var subClaims = tokenClaims[authType];
    rVal = _getInfoTokenClaim(subClaims, claim, type);
  } else {
    rVal = tokenClaims[claim];
  }

  return rVal;
};

// Get the customer id for the user who is delegated into
// the current user.
var getDelegateCustomerId = function getDelegateCustomerId(encrypt) {
  return getDelegateClaim('info_cid', encrypt, 'idp');
};

// Get the customer id from the newest auth token
var getCustomerId = function getCustomerId(encrypt) {
  if (isAuthenticated()) {
    // Search the info token for the customer id claim
    return _cryptOrUndefined(encrypt, _getInfoTokenClaim(_authInfoToken, 'info_cid'));
  }
};

// Fetches the appropriate ID for the newest auth token.
// If it's a delegation token, the subordinate's
// (child's) ID will be returned.
var getUserId = function getUserId(encrypt) {
  if (isAuthenticated()) {
    var tokenType = _authInfoToken.typ;

    // For the auth type (idp vs pass vs jomax), lookup the
    // name of the claim that holds the customer id
    var claimToLookup = _authRealmIdMap[tokenType];

    // Search the info token for the customer id claim
    return _cryptOrUndefined(encrypt, _getInfoTokenClaim(_authInfoToken, claimToLookup));
  }
};

// Fetches the shopper ID for the newest auth token.
// If it's a delegation token, the subordinate's
// (child's) ID will be returned.
var getShopperId = function getShopperId(encrypt) {
  if (isAuthenticated()) {
    var tokenType = _authInfoToken.typ;
    if (tokenType === 'idp') {
      return _cryptOrUndefined(encrypt, _getInfoTokenClaim(_authInfoToken, 'info_shopperId'));
    }
  }
};

// Fetches the claim for the newest auth token.
// If it's a delegation token, the subordinate's
// (child's) claim will be searched.
var getClaimFromInfoToken = function getClaimFromInfoToken(claimName) {
  if (isAuthenticated()) {
    return _getInfoTokenClaim(_authInfoToken, claimName);
  }
};

// First tries to fetch the fpid for the newest auth tokken.
// If not found, fallback/fetch the fpid from the brand cookie
var getFederationPartnerId = function getFederationPartnerId() {
  if (isAuthenticated()) {
    var fpid = _getInfoTokenClaim(_authInfoToken, 'fpid');
    if (!fpid) {
      var tokenType = _authInfoToken.typ;
      if (typeof tokenType !== 'undefined') {
        fpid = (0, _cookie.findCookie)('brand_' + tokenType);
      }
    }
    return fpid;
  }
};

// Fetches the realm for the newest auth token.
// Regardless of whether it's a delegate token,
// the subordinate (child) user's realm is on the root.
// https://confluence.godaddy.com/display/AUTH/Token+Claims
var getUserRealm = function getUserRealm() {
  if (isAuthenticated()) {
    return _authInfoToken.typ;
  }
};

exports.getClaimFromInfoToken = getClaimFromInfoToken;
exports.getDelegateCustomerId = getDelegateCustomerId;
exports.getDelegateEmployeeId = getDelegateEmployeeId;
exports.getEmployeeId = getEmployeeId;
exports.getFederationPartnerId = getFederationPartnerId;
exports.getCustomerId = getCustomerId;
exports.getUserRealm = getUserRealm;
exports.getShopperId = getShopperId;
exports.isAuthenticated = isAuthenticated;
exports.isDelegated = isDelegated;
exports.isEmployee = isEmployee;
exports.updateAuthTokens = updateAuthTokens;
exports.getUserId = getUserId;

// Private exports for testing

exports._getInfoTokenClaims = _getInfoTokenClaims;
exports._getInfoTokenClaim = _getInfoTokenClaim;
exports._getDelegationClaims = _getDelegationClaims;
exports._cryptOrUndefined = _cryptOrUndefined;